import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  MessageCircle,
  Mail,
  HardDrive,
  Phone,
  Settings,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  HelpCircle,
  Sparkles,
  Bot,
  Send,
  RefreshCw,
  Shield,
  Bell,
  Calendar,
  Zap,
  Smartphone,
  Download,
  Share2,
  Workflow,
  ChevronRight,
  Apple,
  Chrome,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

// Composant pour une connexion
const ConnectionCard = ({ 
  title, 
  description, 
  icon: Icon, 
  color, 
  isConnected, 
  onConnect, 
  onDisconnect,
  children,
  tutorial
}) => {
  const [showTutorial, setShowTutorial] = useState(false);
  
  return (
    <Card className={`relative overflow-hidden ${isConnected ? 'border-green-500/50' : ''}`}>
      {isConnected && (
        <div className="absolute top-3 right-3">
          <Badge className="bg-green-500 text-white">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Connecté
          </Badge>
        </div>
      )}
      
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${color}20` }}
          >
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {children}
        
        {tutorial && (
          <div className="pt-2 border-t">
            <button
              onClick={() => setShowTutorial(!showTutorial)}
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <HelpCircle className="w-4 h-4" />
              {showTutorial ? "Masquer l'aide" : "Comment faire ?"}
            </button>
            
            {showTutorial && (
              <div className="mt-3 p-4 bg-muted rounded-lg text-sm space-y-2">
                {tutorial.map((step, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="font-bold text-primary">{i + 1}.</span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Composant Section Influenceurs IA
const InfluenceursIASection = () => {
  const [influenceurs, setInfluenceurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newInfluenceur, setNewInfluenceur] = useState({
    nom: '',
    description: '',
    persona: 'agriculteur',
    specialites: [],
    ton: 'professionnel'
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchInfluenceurs();
  }, []);

  const fetchInfluenceurs = async () => {
    try {
      const response = await api.get('/studio-ia/influenceurs');
      // La réponse contient {predefinis: [...], personnalises: [...]}
      setInfluenceurs(response.data?.personnalises || []);
    } catch (error) {
      console.error('Erreur chargement influenceurs:', error);
      setInfluenceurs([]); // Fallback to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const createInfluenceur = async () => {
    if (!newInfluenceur.nom) {
      toast.error('Le nom est requis');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post('/studio-ia/influenceurs/create', newInfluenceur);
      if (response.data?.success) {
        toast.success('Influenceur créé !');
        setShowCreateDialog(false);
        setNewInfluenceur({ nom: '', description: '', persona: 'agriculteur', specialites: [], ton: 'professionnel' });
        fetchInfluenceurs();
      }
    } catch (error) {
      toast.error('Erreur création');
    } finally {
      setCreating(false);
    }
  };

  const personas = [
    { id: 'agriculteur', nom: 'Agriculteur passionné', emoji: '👨‍🌾' },
    { id: 'expert_bio', nom: 'Expert Bio', emoji: '🌱' },
    { id: 'chef', nom: 'Chef cuisinier', emoji: '👨‍🍳' },
    { id: 'conseiller', nom: 'Conseiller technique', emoji: '📊' },
    { id: 'commercial', nom: 'Commercial terrain', emoji: '💼' }
  ];

  const tons = [
    { id: 'professionnel', nom: 'Professionnel' },
    { id: 'amical', nom: 'Amical & accessible' },
    { id: 'expert', nom: 'Expert & technique' },
    { id: 'passione', nom: 'Passionné & enthousiaste' }
  ];

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            Influenceurs IA
          </h2>
          <p className="text-muted-foreground">
            Créez des personnages IA pour communiquer sur les réseaux sociaux
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Créer un influenceur
        </Button>
      </div>

      {/* Influenceurs prédéfinis */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Influenceurs prédéfinis</CardTitle>
          <CardDescription>Personnages prêts à l'emploi pour vos campagnes</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { id: 'pierre_vigne', nom: 'Pierre Vignes', persona: 'agriculteur', description: 'Viticulteur passionné depuis 3 générations', specialites: ['Viticulture', 'Terroir'] },
                { id: 'marie_bio', nom: 'Marie Bio', persona: 'expert_bio', description: 'Experte en agriculture biologique', specialites: ['Bio', 'Permaculture'] },
                { id: 'jean_tech', nom: 'Jean Tech', persona: 'conseiller', description: 'Conseiller en agriculture de précision', specialites: ['AgTech', 'Données'] }
              ].map((inf) => (
                <Card key={inf.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                        inf.persona === 'agriculteur' ? 'bg-gradient-to-br from-green-400 to-emerald-600' :
                        inf.persona === 'expert_bio' ? 'bg-gradient-to-br from-lime-400 to-green-600' :
                        'bg-gradient-to-br from-blue-400 to-indigo-600'
                      }`}>
                        {inf.nom.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{inf.nom}</h3>
                        <p className="text-sm text-muted-foreground">{inf.description}</p>
                        <div className="flex gap-1 mt-2">
                          {inf.specialites.map((s, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mes influenceurs personnalisés */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Mes influenceurs</CardTitle>
          <CardDescription>Vos personnages IA personnalisés</CardDescription>
        </CardHeader>
        <CardContent>
          {influenceurs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bot className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucun influenceur personnalisé</p>
              <p className="text-sm">Créez votre premier influenceur IA !</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4">
              {influenceurs.map((inf) => (
                <Card key={inf.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center text-white font-bold">
                        {inf.nom?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{inf.nom}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">{inf.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lien vers Studio IA complet */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg">Accéder au Studio IA complet</h3>
              <p className="text-muted-foreground">
                Générez du contenu, créez des vidéos et publiez sur les réseaux sociaux
              </p>
            </div>
            <Button variant="outline" onClick={() => window.location.href = '/studio-ia'}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Ouvrir le Studio
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dialog création influenceur */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Créer un influenceur IA</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom</Label>
              <Input
                value={newInfluenceur.nom}
                onChange={(e) => setNewInfluenceur(prev => ({ ...prev, nom: e.target.value }))}
                placeholder="Ex: Pierre Vignes"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={newInfluenceur.description}
                onChange={(e) => setNewInfluenceur(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Décrivez la personnalité et l'expertise de cet influenceur..."
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div>
              <Label>Persona</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {personas.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setNewInfluenceur(prev => ({ ...prev, persona: p.id }))}
                    className={`p-2 rounded-lg border text-left text-sm flex items-center gap-2 ${
                      newInfluenceur.persona === p.id ? 'border-primary bg-primary/10' : 'hover:border-muted-foreground/50'
                    }`}
                  >
                    <span className="text-xl">{p.emoji}</span>
                    <span>{p.nom}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Ton de communication</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {tons.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setNewInfluenceur(prev => ({ ...prev, ton: t.id }))}
                    className={`p-2 rounded-lg border text-sm ${
                      newInfluenceur.ton === t.id ? 'border-primary bg-primary/10' : 'hover:border-muted-foreground/50'
                    }`}
                  >
                    {t.nom}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annuler</Button>
            <Button onClick={createInfluenceur} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Page Creator
export const CreatorPage = () => {
  const [connections, setConnections] = useState({
    telegram: { connected: false, token: '', botName: '' },
    gmail: { connected: false, email: '' },
    drive: { connected: false, folderId: '' },
    whatsapp: { connected: false, phoneId: '' }
  });
  const [loading, setLoading] = useState({});
  const [showTokens, setShowTokens] = useState({});
  const [backupSettings, setBackupSettings] = useState({
    enabled: false,
    frequency: 'monthly',
    lastBackup: null
  });
  const [serviceClientEnabled, setServiceClientEnabled] = useState(true);
  
  // Charger les connexions existantes
  useEffect(() => {
    fetchConnections();
  }, []);
  
  const fetchConnections = async () => {
    try {
      const response = await api.get('/creator/connections');
      if (response.data) {
        setConnections(prev => ({...prev, ...response.data.connections}));
        setBackupSettings(response.data.backup || backupSettings);
      }
    } catch (error) {
      console.error('Erreur chargement connexions:', error);
    }
  };
  
  // Sauvegarder une connexion
  const saveConnection = async (type, data) => {
    setLoading(prev => ({...prev, [type]: true}));
    
    try {
      const response = await api.post(`/creator/connections/${type}`, data);
      
      if (response.data.success) {
        setConnections(prev => ({
          ...prev,
          [type]: { ...prev[type], ...data, connected: true }
        }));
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} connecté !`);
      } else {
        toast.error(response.data.error || 'Erreur de connexion');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(prev => ({...prev, [type]: false}));
    }
  };
  
  // Déconnecter
  const disconnect = async (type) => {
    try {
      await api.delete(`/creator/connections/${type}`);
      setConnections(prev => ({
        ...prev,
        [type]: { connected: false }
      }));
      toast.success('Déconnecté');
    } catch (error) {
      toast.error('Erreur');
    }
  };
  
  // Copier dans le presse-papier
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };
  
  return (
    <div className="space-y-6" data-testid="creator-page">
      {/* En-tête */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-primary" />
          Creator
        </h1>
        <p className="text-muted-foreground mt-1">
          Connectez vos services et personnalisez votre application
        </p>
      </div>
      
      <Tabs defaultValue="install" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="install">
            <Smartphone className="w-4 h-4 mr-2" />
            Installation
          </TabsTrigger>
          <TabsTrigger value="connections">
            <Zap className="w-4 h-4 mr-2" />
            Connexions
          </TabsTrigger>
          <TabsTrigger value="influenceurs">
            <Sparkles className="w-4 h-4 mr-2" />
            Influenceurs IA
          </TabsTrigger>
          <TabsTrigger value="backup">
            <HardDrive className="w-4 h-4 mr-2" />
            Sauvegardes
          </TabsTrigger>
          <TabsTrigger value="support">
            <Bot className="w-4 h-4 mr-2" />
            Service Client
          </TabsTrigger>
        </TabsList>
        
        {/* Onglet Installation Mobile */}
        <TabsContent value="install" className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">📱 Installez l'application sur votre téléphone</h2>
            <p className="text-muted-foreground">
              Accédez à votre Bureau Agricole depuis votre téléphone, même hors connexion
            </p>
          </div>
          
          {/* Instructions iPhone */}
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/20 dark:to-background">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-blue-600">
                <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                  <Apple className="w-6 h-6" />
                </div>
                iPhone / iPad (Safari)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                {[
                  { step: 1, text: "Ouvrez Safari sur votre iPhone", detail: "L'icône bleue avec une boussole" },
                  { step: 2, text: "Allez sur le site du Bureau Agricole", detail: window.location.origin },
                  { step: 3, text: "Appuyez sur le bouton Partager", detail: "Le carré avec une flèche vers le haut (en bas de l'écran)" },
                  { step: 4, text: "Faites défiler et appuyez sur \"Sur l'écran d'accueil\"", detail: "L'icône avec un + dans un carré" },
                  { step: 5, text: "Appuyez sur \"Ajouter\"", detail: "En haut à droite" },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-3 p-3 bg-white dark:bg-muted rounded-lg shadow-sm">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                      {item.step}
                    </span>
                    <div>
                      <p className="font-medium">{item.text}</p>
                      <p className="text-sm text-muted-foreground">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  💡 <strong>Astuce :</strong> Une fois installé, l'application apparaîtra sur votre écran d'accueil comme une vraie application !
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* Instructions Android */}
          <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white dark:from-green-950/20 dark:to-background">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-green-600">
                <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                  <Chrome className="w-6 h-6" />
                </div>
                Android (Chrome)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                {[
                  { step: 1, text: "Ouvrez Chrome sur votre téléphone Android", detail: "Pas Samsung Internet ou autre" },
                  { step: 2, text: "Allez sur le site du Bureau Agricole", detail: window.location.origin },
                  { step: 3, text: "Appuyez sur les 3 points ⋮ en haut à droite", detail: "Le menu de Chrome" },
                  { step: 4, text: "Appuyez sur \"Ajouter à l'écran d'accueil\"", detail: "Ou \"Installer l'application\"" },
                  { step: 5, text: "Confirmez en appuyant sur \"Ajouter\"", detail: "L'icône apparaîtra sur votre écran" },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-3 p-3 bg-white dark:bg-muted rounded-lg shadow-sm">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm">
                      {item.step}
                    </span>
                    <div>
                      <p className="font-medium">{item.text}</p>
                      <p className="text-sm text-muted-foreground">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-200">
                  💡 <strong>Astuce :</strong> Sur certains téléphones, une bannière "Installer" apparaît automatiquement en bas de l'écran !
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* QR Code pour partager */}
          <Card>
            <CardContent className="pt-6 text-center">
              <h3 className="font-bold mb-2">📲 Partagez avec votre équipe</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Envoyez ce lien à vos collaborateurs pour qu'ils puissent aussi installer l'application
              </p>
              <div className="flex items-center justify-center gap-2">
                <Input 
                  value={window.location.origin} 
                  readOnly 
                  className="max-w-xs text-center"
                />
                <Button variant="outline" onClick={() => {
                  navigator.clipboard.writeText(window.location.origin);
                  toast.success('Lien copié !');
                }}>
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="outline" onClick={() => {
                  if (navigator.share) {
                    navigator.share({ title: 'Bureau Agricole', url: window.location.origin });
                  } else {
                    toast.info('Utilisez le bouton copier');
                  }
                }}>
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Onglet Connexions */}
        <TabsContent value="connections" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            
            {/* Telegram */}
            <ConnectionCard
              title="Telegram"
              description="Messages, appels et notifications"
              icon={MessageCircle}
              color="#0088cc"
              isConnected={connections.telegram?.connected}
              tutorial={[
                "Ouvrez Telegram sur votre téléphone",
                "Cherchez @BotFather dans la barre de recherche",
                "Envoyez-lui le message : /newbot",
                "Choisissez un nom pour votre bot (ex: AgroPlan Assistant)",
                "Choisissez un identifiant (ex: agroplan_bot)",
                "Copiez le code qui ressemble à : 123456789:ABCdef...",
                "Collez-le ci-dessous !"
              ]}
            >
              <div className="space-y-3">
                <div>
                  <Label>Code du Bot (Token)</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="relative flex-1">
                      <Input
                        type={showTokens.telegram ? "text" : "password"}
                        placeholder="123456789:ABCdefGHIjklMNO..."
                        value={connections.telegram?.token || ''}
                        onChange={(e) => setConnections(prev => ({
                          ...prev,
                          telegram: { ...prev.telegram, token: e.target.value }
                        }))}
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowTokens(prev => ({...prev, telegram: !prev.telegram}))}
                      >
                        {showTokens.telegram ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                
                <Button 
                  className="w-full"
                  onClick={() => saveConnection('telegram', { token: connections.telegram?.token })}
                  disabled={loading.telegram || !connections.telegram?.token}
                >
                  {loading.telegram ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : connections.telegram?.connected ? (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  ) : (
                    <MessageCircle className="w-4 h-4 mr-2" />
                  )}
                  {connections.telegram?.connected ? 'Mettre à jour' : 'Connecter Telegram'}
                </Button>
                
                {connections.telegram?.connected && (
                  <Button 
                    variant="outline" 
                    className="w-full text-destructive"
                    onClick={() => disconnect('telegram')}
                  >
                    Déconnecter
                  </Button>
                )}
              </div>
            </ConnectionCard>
            
            {/* Gmail */}
            <ConnectionCard
              title="Gmail"
              description="Envoyer et recevoir des emails"
              icon={Mail}
              color="#ea4335"
              isConnected={connections.gmail?.connected}
              tutorial={[
                "Cliquez sur le bouton 'Connecter Gmail'",
                "Une fenêtre Google va s'ouvrir",
                "Connectez-vous avec votre compte Google",
                "Acceptez les autorisations demandées",
                "C'est fait ! Vos agents peuvent maintenant envoyer des emails"
              ]}
            >
              <div className="space-y-3">
                {connections.gmail?.connected ? (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Connecté en tant que :</p>
                    <p className="font-medium">{connections.gmail?.email || 'Email configuré'}</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Connectez votre compte Gmail pour permettre aux agents d'envoyer des emails en votre nom.
                  </p>
                )}
                
                <Button 
                  className="w-full"
                  onClick={() => {
                    // Ouvrir la fenêtre OAuth Google
                    toast.info("Ouverture de la connexion Google...");
                    // window.open('/api/auth/google', '_blank');
                    saveConnection('gmail', { email: 'demo@gmail.com' }); // Simulation
                  }}
                  disabled={loading.gmail}
                >
                  {loading.gmail ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  {connections.gmail?.connected ? 'Reconnecter' : 'Connecter Gmail'}
                </Button>
                
                {connections.gmail?.connected && (
                  <Button 
                    variant="outline" 
                    className="w-full text-destructive"
                    onClick={() => disconnect('gmail')}
                  >
                    Déconnecter
                  </Button>
                )}
              </div>
            </ConnectionCard>
            
            {/* Google Drive */}
            <ConnectionCard
              title="Google Drive"
              description="Sauvegardes automatiques"
              icon={HardDrive}
              color="#4285f4"
              isConnected={connections.drive?.connected}
              tutorial={[
                "Cliquez sur 'Connecter Drive'",
                "Autorisez l'accès à votre Google Drive",
                "Un dossier 'AgroPlan_Backup' sera créé automatiquement",
                "Vos documents seront sauvegardés chaque mois"
              ]}
            >
              <div className="space-y-3">
                {connections.drive?.connected ? (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Dossier de sauvegarde :</p>
                    <p className="font-medium">📁 AgroPlan_Backup</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Sauvegardez automatiquement tous vos documents sur Google Drive.
                  </p>
                )}
                
                <Button 
                  className="w-full"
                  onClick={() => {
                    toast.info("Ouverture de la connexion Google Drive...");
                    saveConnection('drive', { folderId: 'backup-folder' }); // Simulation
                  }}
                  disabled={loading.drive}
                >
                  {loading.drive ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <HardDrive className="w-4 h-4 mr-2" />
                  )}
                  {connections.drive?.connected ? 'Reconnecter' : 'Connecter Drive'}
                </Button>
                
                {connections.drive?.connected && (
                  <Button 
                    variant="outline" 
                    className="w-full text-destructive"
                    onClick={() => disconnect('drive')}
                  >
                    Déconnecter
                  </Button>
                )}
              </div>
            </ConnectionCard>
            
            {/* WhatsApp */}
            <ConnectionCard
              title="WhatsApp Business"
              description="Messages et appels WhatsApp"
              icon={Phone}
              color="#25d366"
              isConnected={connections.whatsapp?.connected}
              tutorial={[
                "Vous devez avoir un compte Meta Business",
                "Allez sur business.facebook.com",
                "Créez une application WhatsApp Business",
                "Récupérez votre Phone Number ID",
                "Collez-le ci-dessous"
              ]}
            >
              <div className="space-y-3">
                <div>
                  <Label>Phone Number ID</Label>
                  <Input
                    placeholder="123456789012345"
                    value={connections.whatsapp?.phoneId || ''}
                    onChange={(e) => setConnections(prev => ({
                      ...prev,
                      whatsapp: { ...prev.whatsapp, phoneId: e.target.value }
                    }))}
                    className="mt-1"
                  />
                </div>
                
                <Button 
                  className="w-full"
                  onClick={() => saveConnection('whatsapp', { phoneId: connections.whatsapp?.phoneId })}
                  disabled={loading.whatsapp || !connections.whatsapp?.phoneId}
                >
                  {loading.whatsapp ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Phone className="w-4 h-4 mr-2" />
                  )}
                  {connections.whatsapp?.connected ? 'Mettre à jour' : 'Connecter WhatsApp'}
                </Button>
                
                <p className="text-xs text-muted-foreground text-center">
                  💡 WhatsApp est optionnel. Telegram suffit pour la plupart des usages.
                </p>
              </div>
            </ConnectionCard>
            
            {/* Pipedream */}
            <ConnectionCard
              title="Pipedream"
              description="Automatisation avancée (Zapier alternatif)"
              icon={Workflow}
              color="#e91e63"
              isConnected={connections.pipedream?.connected}
              tutorial={[
                "Allez sur pipedream.com et créez un compte gratuit",
                "Cliquez sur 'New Project' puis 'New Workflow'",
                "Choisissez un déclencheur (Trigger), par exemple 'Webhook'",
                "Copiez l'URL du webhook qui ressemble à : https://eo...pipedream.net",
                "Collez cette URL ci-dessous",
                "Dans Pipedream, ajoutez des actions (envoyer email, SMS, etc.)",
                "Vos agents IA pourront déclencher ces automatisations !"
              ]}
            >
              <div className="space-y-3">
                <div>
                  <Label>URL du Webhook Pipedream</Label>
                  <Input
                    placeholder="https://eo...pipedream.net"
                    value={connections.pipedream?.webhook_url || ''}
                    onChange={(e) => setConnections(prev => ({
                      ...prev,
                      pipedream: { ...prev.pipedream, webhook_url: e.target.value }
                    }))}
                    className="mt-1"
                  />
                </div>
                
                <Button 
                  className="w-full"
                  onClick={() => saveConnection('pipedream', { webhook_url: connections.pipedream?.webhook_url })}
                  disabled={loading.pipedream || !connections.pipedream?.webhook_url}
                >
                  {loading.pipedream ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Workflow className="w-4 h-4 mr-2" />
                  )}
                  {connections.pipedream?.connected ? 'Mettre à jour' : 'Connecter Pipedream'}
                </Button>
                
                <div className="text-center">
                  <a 
                    href="https://pipedream.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Créer un compte Pipedream gratuit
                  </a>
                </div>
              </div>
            </ConnectionCard>
            
          </div>
          
          {/* Explications supplémentaires */}
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-primary" />
                Pourquoi connecter ces services ?
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p className="font-medium">📱 Telegram / WhatsApp :</p>
                  <p className="text-muted-foreground">
                    Permet à l'agent "Secrétaire" de vous envoyer des messages, rappels et notifications directement sur votre téléphone.
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="font-medium">📧 Gmail :</p>
                  <p className="text-muted-foreground">
                    Permet aux agents IA d'envoyer des emails en votre nom (devis, relances, confirmations).
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="font-medium">💾 Google Drive :</p>
                  <p className="text-muted-foreground">
                    Sauvegarde automatique de tous vos documents : factures, parcelles, interventions.
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="font-medium">⚡ Pipedream :</p>
                  <p className="text-muted-foreground">
                    Automatisations avancées : poster sur les réseaux sociaux, connecter d'autres apps, etc.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Onglet Influenceurs IA */}
        <TabsContent value="influenceurs" className="space-y-6">
          <InfluenceursIASection />
        </TabsContent>
        
        {/* Onglet Sauvegardes */}
        <TabsContent value="backup" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="w-5 h-5" />
                Sauvegardes automatiques
              </CardTitle>
              <CardDescription>
                Protégez vos données avec des sauvegardes régulières sur Google Drive
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Sauvegardes automatiques</p>
                  <p className="text-sm text-muted-foreground">
                    Vos données seront sauvegardées chaque mois
                  </p>
                </div>
                <Switch 
                  checked={backupSettings.enabled}
                  onCheckedChange={(checked) => setBackupSettings(prev => ({...prev, enabled: checked}))}
                />
              </div>
              
              {backupSettings.enabled && (
                <>
                  <div>
                    <Label>Fréquence des sauvegardes</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {[
                        { id: 'weekly', label: 'Chaque semaine' },
                        { id: 'monthly', label: 'Chaque mois' },
                        { id: 'quarterly', label: 'Chaque trimestre' }
                      ].map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => setBackupSettings(prev => ({...prev, frequency: opt.id}))}
                          className={`p-3 rounded-lg border text-sm transition-colors ${
                            backupSettings.frequency === opt.id 
                              ? 'border-primary bg-primary/10' 
                              : 'hover:border-muted-foreground/50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Dernière sauvegarde</span>
                      <Badge variant="outline">
                        {backupSettings.lastBackup || 'Jamais'}
                      </Badge>
                    </div>
                    <Button variant="outline" className="w-full">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Lancer une sauvegarde maintenant
                    </Button>
                  </div>
                  
                  <div>
                    <Label>Ce qui est sauvegardé :</Label>
                    <div className="mt-2 space-y-2">
                      {[
                        '📄 Factures et documents comptables',
                        '🗺️ Parcelles et assolement',
                        '👥 Équipe et interventions',
                        '💬 Conversations avec les agents IA',
                        '📸 Photos et pièces jointes'
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
              
              {!connections.drive?.connected && (
                <div className="p-4 border border-amber-200 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    ⚠️ Connectez d'abord Google Drive dans l'onglet "Connexions" pour activer les sauvegardes.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Onglet Service Client */}
        <TabsContent value="support" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5" />
                Service Client IA
              </CardTitle>
              <CardDescription>
                Un assistant pour aider vos clients automatiquement
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Activer le service client IA</p>
                  <p className="text-sm text-muted-foreground">
                    Répond aux questions de vos clients 24h/24
                  </p>
                </div>
                <Switch 
                  checked={serviceClientEnabled}
                  onCheckedChange={setServiceClientEnabled}
                />
              </div>
              
              {serviceClientEnabled && (
                <>
                  <div>
                    <Label>Message d'accueil personnalisé</Label>
                    <Textarea
                      placeholder="Bonjour ! Je suis l'assistant de votre exploitation. Comment puis-je vous aider ?"
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <Label>L'IA peut répondre sur :</Label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {[
                        { id: 'factures', label: '📄 Factures', enabled: true },
                        { id: 'interventions', label: '🚜 Interventions', enabled: true },
                        { id: 'parcelles', label: '🗺️ Parcelles', enabled: true },
                        { id: 'meteo', label: '☀️ Météo', enabled: true },
                        { id: 'stocks', label: '📦 Stocks', enabled: false },
                        { id: 'prix', label: '💰 Prix', enabled: false }
                      ].map(item => (
                        <div key={item.id} className="flex items-center gap-2 p-2 rounded border">
                          <Switch defaultChecked={item.enabled} />
                          <span className="text-sm">{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <p className="font-medium mb-2">📧 Notifications</p>
                    <p className="text-sm text-muted-foreground mb-3">
                      Recevez un email quand l'IA ne peut pas répondre à une question
                    </p>
                    <Input placeholder="votre-email@exemple.com" />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          
          {/* Encart Support */}
          <Card className="border-primary/50 bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Besoin d'aide ?</h3>
                  <p className="text-muted-foreground mt-1">
                    Vous avez un problème avec le logiciel ? Une question technique ? 
                    Une nouvelle fonctionnalité à ajouter ?
                  </p>
                  <p className="mt-3 font-medium text-primary">
                    👉 Contactez votre support technique ! Nous sommes là pour vous aider.
                  </p>
                  <Button className="mt-4" variant="outline">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Contacter le Support
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CreatorPage;
